import api, { type RequestBody, multipart } from ".";
import type { PaginatedList } from "src/types/api";
import type { AuthUser } from "src/types/auth";
import type { Entity } from "src/types/inventory";
import type { AccountTier, EntityRecord, Role, StaffMember } from "src/types/settings";

/** A settlement tag from `GET /tags`. */
export interface Tag {
	id?: number | string;
	uuid?: string;
	name?: string;
}

/** Business-level settings from `GET /settings`. */
export interface BusinessSettings {
	vat?: number;
	is_vat_enabled?: boolean;
	is_transaction_fee_enabled?: boolean;
}

/** Per-channel notification toggles. */
export interface NotificationSettings {
	sms?: boolean;
	email?: boolean;
}

/** Which set of staff accounts to read, and which action `updateStaffStatus` performs. */
export type StaffStatus = "active" | "disabled";

export const getTags = () => api.get<Tag[]>("/tags");

export const deleteTag = ({ uuid }: { uuid: string }) => api.delete(`/tags/${uuid}`);

export const createTag = (data: { name: string }) => api.post("/tags", data);

export const getEntities = (params?: { limit?: number; page?: number }) => api.get<PaginatedList<Entity>>("/entities", { params });

export const createEntity = (data: RequestBody) => api.post("/entities", data);

export const editEntity = ({ uuid, ...data }: { uuid?: string } & RequestBody) => api.patch(`/entities/${uuid}`, data);

export const getRoles = (params?: Record<string, unknown>) => api.get<Role[]>("/roles", { params });

export const createRole = (data: RequestBody) => api.post("/roles", data);

export const updateRole = ({ uuid, ...data }: { uuid?: string } & RequestBody) => api.patch(`/roles/${uuid}`, data);

export const deleteRole = ({ uuid }: { uuid: string }) => api.delete(`/roles/${uuid}`);

export const addStaff = (data: RequestBody) => api.post("/users/add-member", data);

/** Curried by status so it can be handed straight to a query function. */
export const getStaff =
	(type: StaffStatus = "active") =>
	(params?: { page?: number; limit?: number }) =>
		api.get<StaffMember[]>(`/users/${type}-accounts`, { params });

/** `type` is the account's *current* status, so "active" disables and "disabled" enables. */
export const updateStaffStatus =
	(type: StaffStatus = "active") =>
	({ uuid }: { uuid: string }) =>
		api.patch(`/users/${uuid}/${type === "active" ? "disable" : "enable"}-account`);

export const assignStaffRoleAndEntity = (data: RequestBody) => api.post("/users/assign-role", data);

export const updateProfile = ({ uuid, ...data }: { uuid?: string } & RequestBody) =>
	api.post<AuthUser>(`/users/${uuid}/update`, data, multipart);

export const updatePassword = (data: RequestBody) => api.patch("/users/change-password", data);

export const getNotifications = () => api.get<NotificationSettings>("/notification-settings");

export const updateNotifications = (data: NotificationSettings) => api.patch("/notification-settings", data);

export const getTiers = () => api.get<AccountTier[]>("/tiers");

export const getBusinessTier = () => api.get<AccountTier>("/tiers/user-business");

export const getSettings = (params?: { businessUuid?: string }) => api.get<BusinessSettings>("/settings", { params });

export const updateSettings = (values: RequestBody) => api.patch("/settings", values);

export const updateCustomizations = (values: RequestBody) => api.patch("/settings/customized-label", values);

export const verifyBVN = (params: { businessUuid?: string; bvn: string }) => api.get("/users/verify-bvn", { params });

/** Store/warehouse record, re-exported so callers can type the entity forms. */
export type { EntityRecord };
